import { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Edit2, Trash2 } from 'lucide-react';
import { TeamMember } from '../../lib/types';
import { EditMemberModal } from './EditMemberModal';

interface TeamMembersSectionProps {
  members: TeamMember[];
  onOrderChange: (memberId: string, newOrder: number) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSave: (member: TeamMember, file: File | null) => Promise<void>;
  onDragEnd: (result: any) => Promise<void>;
}

export function TeamMembersSection({
  members,
  onOrderChange,
  onDelete,
  onSave,
  onDragEnd
}: TeamMembersSectionProps) {
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-4">Current Team Members</h2>
      <p className="text-sm text-gray-600 mb-4">Drag and drop or use the order number to reorder team members</p>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="teamMembers">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-4"
            >
              {members.map((member, index) => (
                <Draggable key={member.id} draggableId={member.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="border rounded-lg p-4 bg-gray-50 cursor-move"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-16">
                          <input
                            type="number"
                            value={member.order}
                            onChange={(e) => onOrderChange(member.id, parseInt(e.target.value))}
                            className="w-full px-2 py-1 border rounded text-center"
                            min="0"
                            max={members.length - 1}
                          />
                        </div>
                        <img
                          src={member.imageUrl}
                          alt={member.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-grow">
                          <h3 className="font-semibold">{member.name}</h3>
                          <p className="text-gray-600">{member.title}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingMember(member)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <Edit2 size={20} />
                          </button>
                          <button
                            onClick={() => onDelete(member.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {editingMember && (
        <EditMemberModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}